import { useEffect, useRef, useState } from 'react'
import {
  fetchPluginInstalls,
  requestPluginInstall,
  subscribePluginInstallResult,
  type PluginInstallResult,
} from '../../api/ws'
import { useAppStore } from '../../store/app'
import type { PluginInstall } from '../../store/types'
import { AuthSchemeBadge } from '../connections/wizardParts'

// Every plugin reported by every host in the caller's working org, with an
// update affordance per row.
//
// Plugins are upserted server-side by the daemon's plugin_registry_report.
// There's no uninstall surface here — to remove a plugin you uninstall it on
// the host and the row's last_seen_at goes stale (rows are intentionally
// never auto-deleted so the operator keeps a record).
//
// Reachable via gear → About → Debug → "Installed Plugins".

// How long to wait for the daemon's outcome report before giving up. The
// install is a GitHub fetch plus signature and hash verification on the host,
// so seconds are normal and this is deliberately generous — it exists to
// catch a host that will never answer, not to bound a slow one.
const INSTALL_RESULT_TIMEOUT_MS = 90_000

export function PluginInstallsSheet() {
  const pluginInstalls = useAppStore((s) => s.pluginInstalls)
  const hosts = useAppStore((s) => s.hosts)
  const connectionStatus = useAppStore((s) => s.connectionStatus)

  // Keyed by `${host_id}:${plugin_slug}` so two hosts running the same plugin
  // don't share a spinner or clobber each other's error.
  const [busy, setBusy] = useState<Record<string, boolean>>({})
  const [results, setResults] = useState<Record<string, PluginInstallResult>>({})

  useEffect(() => {
    if (connectionStatus !== 'connected') return
    fetchPluginInstalls()
  }, [connectionStatus])

  // Timers that give up waiting for a result. Keyed the same as busy.
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const clearTimer = (key: string) => {
    const t = timers.current[key]
    if (t) {
      clearTimeout(t)
      delete timers.current[key]
    }
  }

  useEffect(() => {
    // Results are broadcast to the whole household, so this fires for
    // installs other people started too. That's deliberate: an install
    // changes what every agent on that host can do.
    return subscribePluginInstallResult((res) => {
      const key = `${res.host_id}:${res.plugin_slug}`
      clearTimer(key)
      setResults((r) => ({ ...r, [key]: res }))
      setBusy((b) => ({ ...b, [key]: false }))
    })
  }, [])

  // Don't leave timers running against an unmounted component.
  useEffect(() => {
    const t = timers.current
    return () => {
      Object.values(t).forEach(clearTimeout)
    }
  }, [])

  async function runInstall(row: PluginInstall, upgrade: boolean, allowBreaking = false) {
    const key = `${row.host_id}:${row.plugin_slug}`
    setBusy((b) => ({ ...b, [key]: true }))
    setResults((r) => {
      const next = { ...r }
      delete next[key]
      return next
    })

    const fail = (message: string) => {
      // Synthesise a result so a request that never reached the host renders
      // in the same place as a daemon-side failure. An operator should not
      // have to know which half of the system said no.
      clearTimer(key)
      setResults((r) => ({
        ...r,
        [key]: { host_id: row.host_id, plugin_slug: row.plugin_slug, ok: false, error: message },
      }))
      setBusy((b) => ({ ...b, [key]: false }))
    }

    try {
      const resp = await requestPluginInstall({
        hostID: row.host_id,
        pluginSlug: row.plugin_slug,
        version: upgrade ? row.latest_version : undefined,
        upgrade,
        force: !upgrade,
        allowBreaking,
      })
      if (resp.error) {
        fail(resp.error)
        return
      }
      // Acknowledged: the command reached the host, but the install is a
      // network round trip from there, so stay busy until it reports.
      //
      // Bounded, because "no result" is a state the host can genuinely leave
      // us in: a daemon too old to know this command drops it silently, and a
      // daemon that dies mid-fetch never reports either. Spinning forever
      // makes a broken install indistinguishable from a slow one, which is
      // the same conflation this whole feature exists to avoid.
      clearTimer(key)
      timers.current[key] = setTimeout(() => {
        fail(
          'No response from the host. It may be running an older version of ' +
            'hearth that does not support installing from here — run `hearth update` ' +
            'on it — or its daemon stopped. The install may still have succeeded; ' +
            'check the plugin list.',
        )
      }, INSTALL_RESULT_TIMEOUT_MS)
    } catch (e) {
      fail(String(e))
    }
  }

  // Group by host so the operator can scan "what does host X have."
  const byHost = new Map<string, PluginInstall[]>()
  for (const p of pluginInstalls) {
    const list = byHost.get(p.host_id) ?? []
    list.push(p)
    byHost.set(p.host_id, list)
  }

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      <p style={{ opacity: 0.65, marginTop: 0 }}>
        Plugins give Hearth the ability to connect to external resources, like Home Assistant or Google Drive.
      </p>

      {pluginInstalls.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No plugins reported in this organization.</p>
      ) : (
        [...byHost.entries()].map(([hostID, rows]) => {
          const hostName = hosts.find((h) => h.host_id === hostID)?.hostname ?? hostID
          return (
            <div key={hostID} style={{ marginBottom: 20 }}>
              <h3
                style={{
                  margin: '4px 0',
                  fontSize: 13,
                  opacity: 0.7,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {hostName}
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {rows.map((p) => {
                  const key = `${p.host_id}:${p.plugin_slug}`
                  return (
                    <PluginRow
                      key={p.id}
                      install={p}
                      busy={!!busy[key]}
                      result={results[key]}
                      onInstall={runInstall}
                    />
                  )
                })}
              </ul>
            </div>
          )
        })
      )}
    </div>
  )
}

function PluginRow({
  install,
  busy,
  result,
  onInstall,
}: {
  install: PluginInstall
  busy: boolean
  result?: PluginInstallResult
  onInstall: (install: PluginInstall, upgrade: boolean, allowBreaking?: boolean) => void
}) {
  const stale =
    install.last_seen_at &&
    Date.now() - new Date(install.last_seen_at).getTime() > 24 * 60 * 60 * 1000

  // Only offer catalog actions for plugins the catalog actually carries. A
  // local-archive install has no latest_version and cannot be reinstalled
  // from here; a button that always fails is worse than no button.
  const inCatalog = !!install.latest_version

  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 8,
        }}
      >
        <div style={{ fontWeight: 600 }}>
          {install.display_name || install.plugin_slug}
          {install.auth_scheme && <AuthSchemeBadge scheme={install.auth_scheme} />}
        </div>
        {install.version && (
          <div style={{ opacity: 0.6, fontSize: 12, fontFamily: 'monospace' }}>
            {install.version}
          </div>
        )}
      </div>
      <div style={{ opacity: 0.7, fontSize: 12, fontFamily: 'monospace' }}>
        {install.plugin_slug}
      </div>
      {install.description && (
        <div style={{ opacity: 0.7, fontSize: 12 }}>{install.description}</div>
      )}

      {install.update_available && (
        <div style={{ fontSize: 12, color: '#0a5', fontWeight: 600 }}>
          v{install.latest_version} available
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
        {inCatalog && !result?.breaking && (
          <button
            style={{ fontSize: 12, padding: '4px 10px' }}
            disabled={busy}
            onClick={() => onInstall(install, !!install.update_available)}
          >
            {busy
              ? 'Installing…'
              : install.update_available
                ? `Update to v${install.latest_version}`
                : 'Reinstall'}
          </button>
        )}
        {busy && (
          <span style={{ fontSize: 12, opacity: 0.6 }}>
            fetching and verifying on the host…
          </span>
        )}
      </div>

      {result?.breaking ? (
        <BreakingChangeConfirm
          result={result}
          busy={busy}
          onConfirm={() => onInstall(install, !!install.update_available, true)}
        />
      ) : (
        result && <InstallResultLine result={result} />
      )}

      <div style={{ opacity: 0.5, fontSize: 12 }}>
        {install.last_seen_at ? (
          <>
            last seen {fmtDate(install.last_seen_at)}
            {stale && (
              <span style={{ color: '#900', marginLeft: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                stale
              </span>
            )}
          </>
        ) : (
          'never seen'
        )}
      </div>

      {/* Provenance. Absent for local-archive installs, which genuinely have
          no catalog origin — the blank is honest, not missing data. */}
      {install.catalog_version && (
        <div style={{ opacity: 0.45, fontSize: 11, fontFamily: 'monospace' }}>
          catalog {install.catalog_version}
        </div>
      )}
      <div style={{ opacity: 0.4, fontSize: 12, fontFamily: 'monospace' }}>{install.id}</div>
    </li>
  )
}

// The failure path is the reason this component exists. A successful install
// announces itself by changing the version above; a refused one leaves the row
// untouched, so without this the UI looks identical to "nothing happened".
function InstallResultLine({ result }: { result: PluginInstallResult }) {
  if (result.ok) {
    return (
      <div style={{ fontSize: 12, color: '#0a5' }}>
        Installed v{result.version}
        {result.signature ? ` — ${result.signature}` : ''}
      </div>
    )
  }
  return (
    <div
      style={{
        fontSize: 12,
        color: '#900',
        background: '#fee',
        borderRadius: 4,
        padding: '6px 8px',
      }}
    >
      {result.error || 'Install failed.'}
    </div>
  )
}

function fmtDate(s: string): string {
  const d = new Date(s)
  if (isNaN(d.getTime())) return s
  return d.toLocaleString()
}

// A breaking refusal is a question, not a failure. Nothing broke and nothing
// changed — the host declined to proceed and is asking whether to.
//
// So it renders as a decision with its consequence stated, not as an error
// with a retry button. The distinction matters because the consequence is
// invisible after the fact: removed verbs silently orphan the rules naming
// them, and the symptom (an agent suddenly asking permission for everything)
// shows up much later and looks unrelated.
//
// The rule count and agent names come from the relay, which owns the rules;
// the host only knows which verbs disappeared. When the relay reports zero,
// that is genuinely reassuring and worth saying — it means nothing currently
// depends on what is being removed.
function BreakingChangeConfirm({
  result,
  busy,
  onConfirm,
}: {
  result: PluginInstallResult
  busy: boolean
  onConfirm: () => void
}) {
  const verbs = result.verbs_removed ?? []
  const configKeys = result.config_keys_now_required ?? []
  const ruleCount = result.orphaned_rule_count ?? 0
  const agents = result.affected_agents ?? []

  return (
    <div
      style={{
        marginTop: 6,
        padding: '10px 12px',
        background: '#fff8e6',
        border: '1px solid #e6c86e',
        borderRadius: 6,
        fontSize: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontWeight: 600 }}>This update changes what agents can do</div>

      {verbs.length > 0 && (
        <div>
          Removes {verbs.length === 1 ? 'an action' : 'actions'}:{' '}
          <code style={{ fontFamily: 'monospace' }}>{verbs.join(', ')}</code>
        </div>
      )}

      {configKeys.length > 0 && (
        <div>
          Now requires setup:{' '}
          <code style={{ fontFamily: 'monospace' }}>{configKeys.join(', ')}</code>. Existing
          connections will need reconfiguring.
        </div>
      )}

      {verbs.length > 0 &&
        (ruleCount > 0 ? (
          <div>
            <strong>
              {ruleCount} permission {ruleCount === 1 ? 'rule' : 'rules'} will stop applying
            </strong>
            {agents.length > 0 && <> — affecting {agents.join(', ')}</>}. Those agents will start
            asking for approval again until you re-grant them.
          </div>
        ) : (
          <div style={{ opacity: 0.75 }}>
            No permission rules currently use those actions, so nothing loses access.
          </div>
        ))}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button style={{ fontSize: 12, padding: '4px 10px' }} disabled={busy} onClick={onConfirm}>
          {busy ? 'Updating…' : 'Update anyway'}
        </button>
        <span style={{ opacity: 0.6 }}>Nothing has changed yet.</span>
      </div>
    </div>
  )
}
